import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import {
  ShieldCheck, Search, FileText, DollarSign, CheckCircle2,
  ChevronDown, ChevronUp, Send, Phone, Mail, MapPin,
  ArrowRight, ClipboardCheck, FileCheck, Clock, Menu, X
} from 'lucide-react';

const FORMSUBMIT_ENDPOINT = 'https://formsubmit.co/trueclaimsol@gmail.com';

/* ─── Intersection Observer Hook ─── */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─── Navbar ─── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const links = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'nav-blur bg-white/90 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a href="#top" className="flex items-center gap-2.5" data-testid="logo-link">
            <img src="https://static.prod-images.emergentagent.com/jobs/e588ccff-42d9-460c-b9a8-2276f4cd10f6/images/723144fb86f31a22dd0103290a38eec09ff88443fac4def767a1759453b9c6c3.png" alt="TrueClaim Solutions logo" className="w-9 h-9 object-contain" />
            <span className={`font-heading text-xl font-bold tracking-tight ${scrolled ? 'text-navy-900' : 'text-white'}`}>
              TrueClaim<span className="text-gold-600"> Solutions</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {links.map(l => (
              <a key={l.href} href={l.href} className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-navy-900' : 'text-slate-300 hover:text-white'}`} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}>
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              className={`rounded-md px-6 py-2.5 text-sm font-medium btn-lift ${scrolled ? 'bg-navy-900 text-white' : 'bg-gold-600 text-white'}`}
              data-testid="nav-cta-button"
            >
              Check Eligibility
            </a>
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className={`w-6 h-6 ${scrolled ? 'text-navy-900' : 'text-white'}`} /> : <Menu className={`w-6 h-6 ${scrolled ? 'text-navy-900' : 'text-white'}`} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            {links.map(l => (
              <a key={l.href} href={l.href} className="block text-sm font-medium text-slate-700 py-2" onClick={() => setMobileOpen(false)}>
                {l.label}
              </a>
            ))}
            <a href="#contact" className="block bg-navy-900 text-white text-center rounded-md px-6 py-3 text-sm font-medium" onClick={() => setMobileOpen(false)}>
              Check Eligibility
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ─── Hero ─── */
function Hero() {
  const ref = useFadeIn();
  return (
    <section id="top" className="relative bg-navy-900 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 opacity-90" />
      <div className="absolute inset-0 grain-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div ref={ref} className="fade-section grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-left">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-6">
              <ShieldCheck className="w-4 h-4 text-gold-500" />
              <span className="text-xs font-semibold text-gold-500 uppercase tracking-widest">Trusted Claim Assistance</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-headline">
              Surplus Funds Claim Assistance
              <span className="block text-gold-500 mt-2">No Upfront Cost</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl" data-testid="hero-subheadline">
              TrueClaim Solutions helps eligible individuals prepare and submit surplus funds claims. You pay nothing upfront — we earn 10-20% only if you receive funds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-gold-600 hover:bg-gold-500 text-white rounded-md px-8 py-4 text-lg font-medium btn-lift"
                data-testid="hero-cta-primary"
              >
                Check Eligibility
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 rounded-md px-8 py-4 text-lg font-medium transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Learn More
              </a>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gold-600/20 rounded-2xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1766808982553-d8eb734a58f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxoYXBweSUyMG9sZGVyJTIwY291cGxlJTIwaG9tZSUyMGZpbmFuY2lhbCUyMHBlYWNlfGVufDB8fHx8MTc3MjE1NDMzN3ww&ixlib=rb-4.1.0&q=85"
                alt="Happy elderly couple holding hands at home"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Curved bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" className="w-full"><path d="M0 60V30C360 0 1080 0 1440 30V60H0Z" fill="#F8FAFC"/></svg>
      </div>
    </section>
  );
}

/* ─── What Are Surplus Funds ─── */
function WhatAreSurplusFunds() {
  const ref = useFadeIn();
  return (
    <section className="py-24 lg:py-32 bg-slate-50" data-testid="what-section">
      <div ref={ref} className="fade-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">Understanding Your Rights</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="what-heading">
            What Are Surplus Funds?
          </h2>
          <div className="gold-line mx-auto mb-8" />
          <p className="text-lg text-slate-600 leading-relaxed" data-testid="what-description">
            Surplus funds (also known as excess proceeds) can occur when property-related sales leave remaining funds after obligations are satisfied. These funds are often held by a government agency until claimed by the rightful party — sometimes for years, uncollected.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: DollarSign, title: 'Uncollected Funds', desc: 'Government agencies may hold excess proceeds from property sales, often without notifying the rightful claimant.' },
            { icon: Search, title: 'Difficult to Locate', desc: 'Surplus funds records can be buried in county records, court filings, and municipal databases — making them hard to identify.' },
            { icon: FileText, title: 'Complex Claim Process', desc: 'Filing a claim requires gathering specific documents, meeting deadlines, and navigating agency-specific procedures.' },
          ].map((item, i) => (
            <div key={i} className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm card-hover" data-testid={`what-card-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-navy-900/5 flex items-center justify-center mb-5">
                <item.icon className="w-6 h-6 text-gold-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-navy-900 mb-3">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── How We Help ─── */
function HowWeHelp() {
  const ref = useFadeIn();
  const services = [
    { icon: ClipboardCheck, text: 'Confirm claim requirements' },
    { icon: FileText, text: 'Organize required documents' },
    { icon: FileCheck, text: 'Prepare complete claim packages' },
    { icon: Send, text: 'Submit claims properly' },
    { icon: Clock, text: 'Track status and respond to agency requests' },
  ];

  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="help-section">
      <div ref={ref} className="fade-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">Our Service</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="help-heading">
              How TrueClaim Solutions Helps
            </h2>
            <div className="gold-line mb-8" />
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Filing a surplus funds claim involves extensive back-and-forth with government agencies, strict documentation requirements, and precise procedures. A single mistake can lead to delays or denial. We handle the entire process so you don't have to.
            </p>
            <ul className="space-y-4">
              {services.map((s, i) => (
                <li key={i} className="flex items-start gap-4" data-testid={`help-item-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-navy-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <s.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </div>
                  <span className="text-slate-700 text-lg">{s.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-slate-100 rounded-2xl" />
            <img
              src="https://images.pexels.com/photos/6170758/pexels-photo-6170758.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Close-up of professional handshake"
              className="relative rounded-2xl shadow-lg w-full h-auto object-cover"
              data-testid="help-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ─── */
function HowItWorks() {
  const ref = useFadeIn();
  const steps = [
    { num: '01', title: 'Eligibility Review', desc: 'We review your information to determine if surplus funds may be available for your claim.', icon: Search },
    { num: '02', title: 'Document Checklist', desc: 'We provide a clear list of exactly which documents are needed for your specific claim.', icon: ClipboardCheck },
    { num: '03', title: 'Claim Preparation', desc: 'We organize everything and prepare a complete, properly formatted claim package.', icon: FileCheck },
    { num: '04', title: 'Submission & Follow-Up', desc: 'We submit your claim to the correct agency and handle all follow-up communications.', icon: Send },
    { num: '05', title: 'Agency Decision & Payment', desc: 'The agency reviews and approves the claim. Funds are paid directly to you.', icon: DollarSign },
  ];

  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-slate-50" data-testid="process-section">
      <div ref={ref} className="fade-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">The Process</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="process-heading">
            How It Works
          </h2>
          <div className="gold-line mx-auto mb-8" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A straightforward, guided process from start to finish. We handle the complexity so you can focus on what matters.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((step, i) => (
            <div key={i} className="relative" data-testid={`step-${i}`}>
              <div className="bg-white p-6 rounded-xl border border-slate-200 card-hover text-center h-full">
                <div className="w-14 h-14 rounded-full bg-navy-900 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                </div>
                <span className="text-xs font-bold text-gold-600 uppercase tracking-widest">Step {step.num}</span>
                <h3 className="font-heading text-lg font-semibold text-navy-900 mt-2 mb-3">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-6 h-6 text-slate-300" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing ─── */
function Pricing() {
  const ref = useFadeIn();
  return (
    <section id="pricing" className="py-24 lg:py-32 bg-white" data-testid="pricing-section">
      <div ref={ref} className="fade-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">Simple & Fair</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="pricing-heading">
            Pricing Transparency
          </h2>
          <div className="gold-line mx-auto" />
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-navy-900 rounded-2xl p-10 lg:p-12 text-center relative overflow-hidden" data-testid="pricing-card">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold-600/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <h3 className="font-heading text-3xl font-bold text-white mb-2">Contingency-Based</h3>
              <p className="text-slate-400 text-lg mb-8">You only pay when you get paid</p>

              <div className="space-y-5 text-left max-w-md mx-auto">
                {[
                  'No upfront cost — ever',
                  'No hidden fees or charges',
                  '10-20% contingency fee, only if you receive funds',
                  'Funds are paid directly to you by the agency',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`pricing-item-${i}`}>
                    <CheckCircle2 className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-white text-lg">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-gold-600 hover:bg-gold-500 text-white rounded-md px-8 py-4 text-lg font-medium mt-10 btn-lift"
                data-testid="pricing-cta"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Trust ─── */
function Trust() {
  const ref = useFadeIn();
  const items = [
    { icon: ShieldCheck, title: 'You Stay in Control', desc: 'The government agency pays you directly. Your funds never pass through us.' },
    { icon: DollarSign, title: 'Clear Fee Structure', desc: 'Our fee is 10-20% of recovered funds — no surprises, no hidden costs.' },
    { icon: FileText, title: 'Privacy-Focused', desc: 'Your personal information is handled with care and used solely for claim processing.' },
    { icon: CheckCircle2, title: 'Full Transparency', desc: 'No guarantees are made — eligibility is determined by the agency. We simply assist with the process.' },
  ];

  return (
    <section className="py-24 lg:py-32 bg-slate-50" data-testid="trust-section">
      <div ref={ref} className="fade-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">Our Promise</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="trust-heading">
            Trust & Transparency
          </h2>
          <div className="gold-line mx-auto" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {items.map((item, i) => (
            <div key={i} className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm card-hover flex gap-5" data-testid={`trust-card-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-gold-600/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-6 h-6 text-gold-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-navy-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ ─── */
function FAQ() {
  const ref = useFadeIn();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: 'How do I know if I have surplus funds?',
      a: 'Surplus funds can result from foreclosure sales, tax lien sales, or other property-related transactions where the sale price exceeded the amount owed. County records, court filings, and public databases may indicate whether funds are available. We can help review your situation during the eligibility check.'
    },
    {
      q: 'Is there an upfront cost?',
      a: 'No. TrueClaim Solutions operates on a contingency basis. There is no upfront cost, no retainer, and no fee unless you receive funds. Our fee is 10-20% of the recovered amount, paid only after you receive your funds.'
    },
    {
      q: 'Who gets paid first?',
      a: 'You do. The agency sends the approved funds directly to you. Our fee is settled after you have received your funds.'
    },
    {
      q: 'How long does the process take?',
      a: 'Timelines vary depending on the agency and jurisdiction. Some claims are processed in weeks, while others may take several months. Government agencies have their own review schedules. We work to ensure your claim is submitted correctly the first time to avoid unnecessary delays.'
    },
    {
      q: 'What documents are typically needed?',
      a: 'Common requirements include government-issued ID, proof of address, documentation establishing your connection to the property (deed, will, trust documents), and sometimes additional court filings. Requirements vary by jurisdiction and agency. We provide a specific checklist tailored to your claim.'
    },
    {
      q: 'Can I file the claim myself?',
      a: 'Yes, you have the right to file a surplus funds claim on your own. However, the process often involves identifying the correct agency, understanding jurisdiction-specific rules, gathering extensive documentation, meeting strict deadlines, and corresponding back and forth with government offices. Many claims are denied or delayed due to incomplete submissions, incorrect forms, or missed procedural steps. Our service is designed to handle these complexities for you.'
    },
    {
      q: 'Is this legal advice?',
      a: 'No. TrueClaim Solutions is not a law firm and does not provide legal advice. We provide claim preparation and submission assistance. If you need legal counsel, we recommend consulting with a licensed attorney in your jurisdiction.'
    },
  ];

  return (
    <section id="faq" className="py-24 lg:py-32 bg-white" data-testid="faq-section">
      <div ref={ref} className="fade-section max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">Common Questions</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="faq-heading">
            Frequently Asked Questions
          </h2>
          <div className="gold-line mx-auto" />
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`faq-item-${i}`}>
              <button
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-medium text-navy-900 pr-4">{faq.q}</span>
                {openIndex === i ? (
                  <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              <div className={`faq-answer px-5 pb-5 ${openIndex === i ? 'open' : ''}`}>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Contact Form ─── */
function ContactForm() {
  const ref = useFadeIn();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', mailing_address: '',
    property_address: '', relationship: '', notes: '', consent: false
  });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setError('Please provide consent to continue.'); return; }
    setError('');
    setStatus('loading');
    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...form,
          _subject: 'New Claim Inquiry from TrueClaim Solutions Website',
          _template: 'table',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Submission failed');
      }
      setStatus('success');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <section id="contact" className="py-24 lg:py-32 bg-slate-50" data-testid="contact-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm" data-testid="form-success-message">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h3 className="font-heading text-3xl font-semibold text-navy-900 mb-4">Thank You</h3>
            <p className="text-lg text-slate-600 leading-relaxed">
              Thank you for reaching out to TrueClaim Solutions. We will review your information and contact you shortly.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 lg:py-32 bg-slate-50" data-testid="contact-section">
      <div ref={ref} className="fade-section max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold text-gold-600 uppercase tracking-widest mb-4 block">Get Started</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy-900 tracking-tight mb-6" data-testid="contact-heading">
            Check Your Eligibility
          </h2>
          <div className="gold-line mx-auto mb-6" />
          <p className="text-lg text-slate-600">
            Fill out the form below and we will review your information.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 lg:p-10 border border-slate-100 shadow-sm space-y-6" data-testid="eligibility-form">
          {/* Name */}
          <div>
            <label htmlFor="name" className="text-sm font-medium text-navy-900 mb-2 block">Full Name *</label>
            <input id="name" name="name" type="text" required value={form.name} onChange={handleChange}
              className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-4 py-2 text-sm placeholder:text-slate-400 transition-all"
              placeholder="John Doe" data-testid="form-name-input" />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="text-sm font-medium text-navy-900 mb-2 block">Email Address *</label>
            <input id="email" name="email" type="email" required value={form.email} onChange={handleChange}
              className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-4 py-2 text-sm placeholder:text-slate-400 transition-all"
              placeholder="john@example.com" data-testid="form-email-input" />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="text-sm font-medium text-navy-900 mb-2 block">Phone Number (optional)</label>
            <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
              className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-4 py-2 text-sm placeholder:text-slate-400 transition-all"
              placeholder="(555) 123-4567" data-testid="form-phone-input" />
          </div>

          {/* Mailing Address */}
          <div>
            <label htmlFor="mailing_address" className="text-sm font-medium text-navy-900 mb-2 block">Mailing Address *</label>
            <input id="mailing_address" name="mailing_address" type="text" required value={form.mailing_address} onChange={handleChange}
              className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-4 py-2 text-sm placeholder:text-slate-400 transition-all"
              placeholder="123 Main St, City, State ZIP" data-testid="form-mailing-input" />
          </div>

          {/* Property Address */}
          <div>
            <label htmlFor="property_address" className="text-sm font-medium text-navy-900 mb-2 block">Property Address (if known)</label>
            <input id="property_address" name="property_address" type="text" value={form.property_address} onChange={handleChange}
              className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-4 py-2 text-sm placeholder:text-slate-400 transition-all"
              placeholder="456 Property Ave, City, State ZIP" data-testid="form-property-input" />
          </div>

          {/* Relationship */}
          <div>
            <label htmlFor="relationship" className="text-sm font-medium text-navy-900 mb-2 block">Relationship to Property *</label>
            <select id="relationship" name="relationship" required value={form.relationship} onChange={handleChange}
              className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-4 py-2 text-sm transition-all"
              data-testid="form-relationship-select">
              <option value="">Select relationship</option>
              <option value="owner">Property Owner</option>
              <option value="heir">Heir</option>
              <option value="trust">Trust Beneficiary</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="text-sm font-medium text-navy-900 mb-2 block">Additional Notes</label>
            <textarea id="notes" name="notes" rows={4} value={form.notes} onChange={handleChange}
              className="flex w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm placeholder:text-slate-400 transition-all resize-none"
              placeholder="Any additional details about your claim..." data-testid="form-notes-textarea" />
          </div>

          {/* Consent */}
          <div className="flex items-start gap-3">
            <input id="consent" name="consent" type="checkbox" checked={form.consent} onChange={handleChange}
              className="custom-checkbox mt-1 w-5 h-5 rounded border-slate-300" data-testid="form-consent-checkbox" />
            <label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed">
              I consent to TrueClaim Solutions contacting me regarding my potential surplus funds claim. I understand this is not legal advice and no guarantee of recovery is made.
            </label>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 rounded-lg p-4 text-sm" data-testid="form-error-message">{error}</div>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full bg-navy-900 hover:bg-navy-800 text-white rounded-md px-8 py-4 text-lg font-medium btn-lift disabled:opacity-70 flex items-center justify-center gap-2"
            data-testid="form-submit-button"
          >
            {status === 'loading' ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full spinner" />
                Submitting...
              </>
            ) : (
              <>
                Submit Eligibility Check
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="bg-navy-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img src="https://static.prod-images.emergentagent.com/jobs/e588ccff-42d9-460c-b9a8-2276f4cd10f6/images/723144fb86f31a22dd0103290a38eec09ff88443fac4def767a1759453b9c6c3.png" alt="TrueClaim Solutions logo" className="w-8 h-8 brightness-0 invert" />
              <span className="font-heading text-lg font-bold tracking-tight">
                TrueClaim<span className="text-gold-500"> Solutions</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Professional surplus funds claim assistance. Helping eligible individuals recover what may rightfully belong to them.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: 'How It Works', href: '#how-it-works' },
                { label: 'Pricing', href: '#pricing' },
                { label: 'FAQ', href: '#faq' },
                { label: 'Check Eligibility', href: '#contact' },
                { label: 'Privacy Policy', href: '/privacy-policy.html' },
                { label: 'Terms of Use', href: '/terms-of-use.html' },
              ].map(l => (
                <li key={l.href}>
                  <a href={l.href} className="text-slate-400 hover:text-white text-sm transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}>
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Contact</h4>
            <div className="space-y-3">
              <a href="mailto:trueclaimsol@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4" />
                trueclaimsol@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Disclaimers */}
        <div className="border-t border-slate-800 mt-12 pt-8" data-testid="footer-disclaimers">
          <div className="text-xs text-slate-500 space-y-2 leading-relaxed">
            <p>TrueClaim Solutions is not a law firm and does not provide legal advice. We provide claim preparation and submission assistance services only.</p>
            <p>No guarantee of recovery is made. Eligibility and approval of surplus funds claims are determined solely by the relevant government agency.</p>
            <p>Your personal information is handled with care and used solely for the purpose of assisting with your claim. We do not sell or share your data with third parties.</p>
            <p className="pt-2 text-slate-600">&copy; {new Date().getFullYear()} TrueClaim Solutions. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── App ─── */
function App() {
  return (
    <div className="font-body">
      <Navbar />
      <Hero />
      <WhatAreSurplusFunds />
      <HowWeHelp />
      <HowItWorks />
      <Pricing />
      <Trust />
      <FAQ />
      <ContactForm />
      <Footer />
    </div>
  );
}

export default App;
